import './GroupAdminMcp.css';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';

import { isMobile } from '../state';
import { McpConnectionAccess } from './GroupCredentialAccess';
import { openCredentials } from '../credentials-navigation';
import { MobileDialog, MobileDialogFooter } from './MobileDialog';
import { McpAuthenticationError, parseMcpAuthentication, type McpAuthentication } from '../../../../shared/mcp-authentication';

interface McpStdioServerDto {
  type?: 'stdio';
  command: string;
  args?: string[];
  env?: Record<string, string>;
  instructions?: string;
  timeout?: number;
}

interface McpHttpServerDto {
  type: 'http' | 'sse';
  url: string;
  headers?: Record<string, string>;
  instructions?: string;
  timeout?: number;
  authentication?: McpAuthentication;
}

export type McpServerConfigDto = McpStdioServerDto | McpHttpServerDto;

interface McpTransportDrafts {
  stdio?: McpStdioServerDto;
  remote?: McpHttpServerDto;
}

export interface McpProbeResultDto {
  ok: boolean;
  latencyMs: number;
  phase?: 'input' | 'container' | 'connect' | 'tools/list';
  error?: string;
  serverInfo?: { name: string; version: string };
  tools?: string[];
}

const MCP_NAME_RE = /^[A-Za-z_][A-Za-z0-9_.-]*$/;

export function McpServersSection({
  value,
  busy,
  onChange,
  onTest,
  credentialsGid,
}: {
  value: Record<string, McpServerConfigDto>;
  busy: boolean;
  onChange: (next: Record<string, McpServerConfigDto>) => void;
  onTest: (name: string, server: McpServerConfigDto) => Promise<McpProbeResultDto>;
  /** Shows each HTTP server's key / sign-in status from the credential gateway. */
  credentialsGid?: string;
  savedServers?: Record<string, McpServerConfigDto>;
}): JSX.Element {
  const mobile = isMobile.value;
  const names = Object.keys(value);
  const [selectedName, setSelectedName] = useState<string | null>(() => names[0] ?? null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<'stdio' | 'http' | 'sse'>('stdio');
  const [newConnection, setNewConnection] = useState('');
  const addButtonRef = useRef<HTMLButtonElement | null>(null);
  const transportDrafts = useRef(new Map<string, McpTransportDrafts>());
  const newConnections = useRef({ stdio: '', remote: '' });
  const trimmedNew = newName.trim();
  const trimmedConnection = newConnection.trim();
  const newNameInvalid = trimmedNew !== '' && !MCP_NAME_RE.test(trimmedNew);
  const newNameDup = trimmedNew !== '' && trimmedNew in value;
  const newConnectionInvalid = newType !== 'stdio'
    && trimmedConnection !== ''
    && !/^https?:\/\//.test(trimmedConnection);
  const canAdd = trimmedNew !== ''
    && !newNameInvalid
    && !newNameDup
    && trimmedConnection !== ''
    && !newConnectionInvalid;
  const selectedValue = selectedName ? value[selectedName] : undefined;

  useEffect(() => {
    if (selectedName && selectedName in value) return;
    setSelectedName(names[0] ?? null);
  }, [JSON.stringify(names)]);

  useEffect(() => {
    if (!mobile) setEditorOpen(false);
  }, [mobile]);

  function addServer(): void {
    if (!canAdd) return;
    const config: McpServerConfigDto = newType === 'stdio'
      ? { type: 'stdio', command: trimmedConnection }
      : { type: newType, url: trimmedConnection };
    transportDrafts.current.delete(trimmedNew);
    onChange({ ...value, [trimmedNew]: config });
    setSelectedName(trimmedNew);
    setNewName('');
    setNewConnection('');
    setAddOpen(false);
    if (mobile) setEditorOpen(true);
  }

  function removeServer(name: string): void {
    transportDrafts.current.delete(name);
    const next = { ...value };
    delete next[name];
    onChange(next);
    const nextName = Object.keys(next)[0] ?? null;
    setSelectedName(nextName);
    setEditorOpen(false);
  }

  function updateServer(name: string, patch: McpServerConfigDto): void {
    onChange({ ...value, [name]: patch });
  }

  function changeServerType(name: string, current: McpServerConfigDto, next: 'stdio' | 'http' | 'sse'): void {
    if (next === mcpServerType(current)) return;
    const drafts = transportDrafts.current.get(name) ?? {};
    if ('url' in current) drafts.remote = current;
    else drafts.stdio = current;
    transportDrafts.current.set(name, drafts);
    const shared = { instructions: current.instructions, timeout: current.timeout };
    updateServer(name, next === 'stdio'
      ? { ...drafts.stdio, type: 'stdio', command: drafts.stdio?.command ?? '', ...shared }
      : { ...drafts.remote, type: next, url: drafts.remote?.url ?? '', ...shared });
  }

  function selectServer(name: string): void {
    setSelectedName(name);
    setAddOpen(false);
    if (mobile) setEditorOpen(true);
  }

  function beginAdd(): void {
    newConnections.current = { stdio: '', remote: '' };
    setNewName('');
    setNewType('stdio');
    setNewConnection('');
    setAddOpen(true);
  }

  function cancelAdd(): void {
    setAddOpen(false);
    requestAnimationFrame(() => addButtonRef.current?.focus());
  }

  const addForm = (
    <McpAddServerForm
      name={newName}
      type={newType}
      connection={newConnection}
      mobile={mobile}
      disabled={busy}
      invalid={newNameInvalid}
      duplicate={newNameDup}
      connectionInvalid={newConnectionInvalid}
      canAdd={canAdd}
      onNameChange={setNewName}
      onTypeChange={(type) => {
        newConnections.current[newType === 'stdio' ? 'stdio' : 'remote'] = newConnection;
        setNewType(type);
        setNewConnection(newConnections.current[type === 'stdio' ? 'stdio' : 'remote']);
      }}
      onConnectionChange={setNewConnection}
      onAdd={addServer}
      onCancel={cancelAdd}
    />
  );

  const editor = selectedName && selectedValue ? (
    <McpServerEditor
      key={selectedName}
      name={selectedName}
      value={selectedValue}
      disabled={busy}
      onChange={(next) => updateServer(selectedName, next)}
      onTypeChange={(next) => changeServerType(selectedName, selectedValue, next)}
      onRemove={() => removeServer(selectedName)}
      onTest={() => onTest(selectedName, selectedValue)}
      auth={credentialsGid && mcpServerType(selectedValue) !== 'stdio' ? (
          <McpConnectionAccess
            gid={credentialsGid}
            resourceUrl={(selectedValue as McpHttpServerDto).url}
            serverName={selectedName}
            disabled={busy}
            authentication={(selectedValue as McpHttpServerDto).authentication}
            onAuthenticationChange={authentication => updateServer(selectedName, { ...selectedValue as McpHttpServerDto, authentication })}
            onOpenCredentials={(create, connectionId) => openCredentials({
              create, connectionId, resourceUrl: (selectedValue as McpHttpServerDto).url,
            })}
          />
      ) : null}
    />
  ) : null;

  return (
    <div class="ga-mcp-section">
      <div class="ga-mcp-section-head">
        <p class="group-admin-help">
          Extra tools this agent can use. Changes take effect after the agent restarts.
        </p>
        <button ref={addButtonRef} type="button" class="ga-mcp-add" disabled={busy} onClick={beginAdd}>
          + Add tools
        </button>
      </div>

      {mobile ? (
        <McpServerList names={names} value={value} selectedName={selectedName} onSelect={selectServer} />
      ) : (
        <div class="ga-mcp-workspace">
          <McpServerList names={names} value={value} selectedName={selectedName} onSelect={selectServer} />
          <div class="ga-mcp-editor-pane">
            {addOpen ? addForm : editor ?? (
              <div class="ga-mcp-empty">
                <strong>No extra tools yet</strong>
                <span>Add tools for this agent to use.</span>
                <button type="button" disabled={busy} onClick={beginAdd}>+ Add tools</button>
              </div>
            )}
          </div>
        </div>
      )}

      {mobile && addOpen ? (
        <MobileDialog
          title="Add tools"
          onBack={cancelAdd}
          backLabel="Back to Tools"
          onClose={cancelAdd}
        >
          <div class="settings-body ga-mcp-mobile-editor">{addForm}</div>
          <MobileDialogFooter>
            <button type="button" onClick={cancelAdd}>Cancel</button>
            <button type="submit" form="ga-mcp-add-server-form" class="primary" disabled={busy || !canAdd}>
              Add tools
            </button>
          </MobileDialogFooter>
        </MobileDialog>
      ) : null}

      {mobile && editorOpen && editor ? (
        <MobileDialog
          title={selectedName ? `${selectedName} tools` : 'Tools'}
          onBack={() => setEditorOpen(false)}
          backLabel="Back to Tools"
          onClose={() => setEditorOpen(false)}
        >
          <div class="settings-body ga-mcp-mobile-editor">{editor}</div>
        </MobileDialog>
      ) : null}
    </div>
  );
}

function mcpServerType(value: McpServerConfigDto): 'stdio' | 'http' | 'sse' {
  const rawType = (value as { type?: string }).type;
  return rawType === 'http' || rawType === 'sse' ? rawType : 'stdio';
}

function mcpServerSummary(value: McpServerConfigDto): string {
  const type = mcpServerType(value);
  if (type === 'stdio') {
    const stdio = value as McpStdioServerDto;
    return [stdio.command, ...(stdio.args ?? [])].filter(Boolean).join(' ') || 'Command required';
  }
  return (value as McpHttpServerDto).url || 'URL required';
}

function mcpServerIssue(value: McpServerConfigDto): string | null {
  const type = mcpServerType(value);
  if (type === 'stdio' && !(value as McpStdioServerDto).command.trim()) return 'Command required';
  if (type !== 'stdio') {
    const url = (value as McpHttpServerDto).url.trim();
    if (!url) return 'URL required';
    if (!/^https?:\/\//.test(url)) return 'Invalid URL';
    try {
      parseMcpAuthentication((value as McpHttpServerDto).authentication, url);
    }
    catch (error) { if (!(error instanceof McpAuthenticationError)) throw error; return error.message; }
  }
  if (value.timeout != null && (value.timeout < 1000 || value.timeout > 600000)) return 'Invalid timeout';
  return null;
}

function McpServerList({
  names,
  value,
  selectedName,
  onSelect,
}: {
  names: string[];
  value: Record<string, McpServerConfigDto>;
  selectedName: string | null;
  onSelect: (name: string) => void;
}): JSX.Element {
  return (
    <nav class="ga-mcp-list" aria-label="Configured tools">
      {names.length === 0 ? (
        <p class="ga-mcp-list-empty">No extra tools yet.</p>
      ) : names.map((name) => {
        const config = value[name]!;
        const issue = mcpServerIssue(config);
        return (
          <button
            type="button"
            key={name}
            class={`ga-mcp-list-item${selectedName === name ? ' active' : ''}`}
            aria-current={selectedName === name ? 'true' : undefined}
            onClick={() => onSelect(name)}
          >
            <span class="ga-mcp-list-name">{name}</span>
            <span class="ga-mcp-list-summary">
              {mcpServerType(config).toUpperCase()} · {mcpServerSummary(config)}
            </span>
            {issue ? <span class="ga-mcp-list-issue">{issue}</span> : null}
            <span class="ga-mcp-list-chevron" aria-hidden="true">{'\u203A'}</span>
          </button>
        );
      })}
    </nav>
  );
}

function McpAddServerForm({
  name,
  type,
  connection,
  mobile,
  disabled,
  invalid,
  duplicate,
  connectionInvalid,
  canAdd,
  onNameChange,
  onTypeChange,
  onConnectionChange,
  onAdd,
  onCancel,
}: {
  name: string;
  type: 'stdio' | 'http' | 'sse';
  connection: string;
  mobile: boolean;
  disabled: boolean;
  invalid: boolean;
  duplicate: boolean;
  connectionInvalid: boolean;
  canAdd: boolean;
  onNameChange: (name: string) => void;
  onTypeChange: (type: 'stdio' | 'http' | 'sse') => void;
  onConnectionChange: (connection: string) => void;
  onAdd: () => void;
  onCancel: () => void;
}): JSX.Element {
  const nameError = invalid
    ? 'Start with a letter or _, then use letters, digits, _, ., or -.'
    : duplicate
      ? `Tools named "${name.trim()}" already exist.`
      : null;
  const connectionLabel = type === 'stdio' ? 'Command' : 'URL';
  const connectionPlaceholder = type === 'stdio'
    ? 'e.g. npx @modelcontextprotocol/server-filesystem'
    : 'https://example.com/mcp';
  const transportHelp = type === 'stdio'
    ? 'Runs a local process inside the agent container.'
    : type === 'http'
      ? 'Connects to a Streamable HTTP endpoint.'
      : 'Connects using the legacy SSE transport.';

  return (
    <form
      id="ga-mcp-add-server-form"
      class="ga-mcp-add-form"
      onSubmit={(event) => { event.preventDefault(); onAdd(); }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          onCancel();
        }
      }}
    >
      {!mobile ? <div>
        <h3>Add tools</h3>
        <p class="group-admin-help">Configure the connection now; advanced settings remain available after adding.</p>
      </div> : null}
      <label class="ga-mcp-row">
        <span class="ga-mcp-row-label">Tools name</span>
        <input
          type="text"
          class={`ga-mcp-input${nameError ? ' ga-mcp-input-invalid' : ''}`}
          placeholder="e.g. context7 or home_assistant"
          value={name}
          disabled={disabled}
          autoFocus
          aria-invalid={nameError ? 'true' : undefined}
          aria-describedby={nameError ? 'ga-mcp-add-name-error' : undefined}
          onInput={(e: JSX.TargetedEvent<HTMLInputElement>) => onNameChange(e.currentTarget.value)}
        />
        {nameError ? <span id="ga-mcp-add-name-error" class="ga-mcp-field-error">{nameError}</span> : null}
      </label>
      <div class="ga-mcp-add-transport">
        <span class="ga-mcp-row-label">Transport</span>
        <McpTransportControl value={type} disabled={disabled} onChange={onTypeChange} />
        <span class="group-admin-help">{transportHelp}</span>
      </div>
      <label class="ga-mcp-row">
        <span class="ga-mcp-row-label">{connectionLabel}</span>
        <input
          type={type === 'stdio' ? 'text' : 'url'}
          class={`ga-mcp-input${connectionInvalid ? ' ga-mcp-input-invalid' : ''}`}
          placeholder={connectionPlaceholder}
          value={connection}
          disabled={disabled}
          aria-invalid={connectionInvalid ? 'true' : undefined}
          aria-describedby={connectionInvalid ? 'ga-mcp-add-connection-error' : undefined}
          onInput={(event: JSX.TargetedEvent<HTMLInputElement>) => onConnectionChange(event.currentTarget.value)}
        />
        {connectionInvalid ? (
          <span id="ga-mcp-add-connection-error" class="ga-mcp-field-error">URL must start with http:// or https://.</span>
        ) : null}
      </label>
      {!mobile ? (
        <div class="ga-mcp-form-actions">
          <button type="button" onClick={onCancel}>Cancel</button>
          <button type="submit" class="primary" disabled={disabled || !canAdd}>Add tools</button>
        </div>
      ) : null}
    </form>
  );
}

function McpServerEditor({
  name,
  value,
  disabled,
  onChange,
  onTypeChange,
  onRemove,
  onTest,
  auth,
}: {
  name: string;
  value: McpServerConfigDto;
  disabled: boolean;
  onChange: (next: McpServerConfigDto) => void;
  onTypeChange: (next: 'stdio' | 'http' | 'sse') => void;
  onRemove: () => void;
  onTest: () => Promise<McpProbeResultDto>;
  auth?: JSX.Element | null;
}): JSX.Element {
  const type = mcpServerType(value);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<McpProbeResultDto | null>(null);

  useEffect(() => setTestResult(null), [JSON.stringify(value)]);

  async function testConnection(): Promise<void> {
    setTesting(true);
    setTestResult(null);
    try {
      setTestResult(await onTest());
    } catch (error) {
      setTestResult({
        ok: false,
        latencyMs: 0,
        phase: 'container',
        error: error instanceof Error ? error.message : 'Unable to check these tools',
      });
    } finally {
      setTesting(false);
    }
  }

  return (
    <div class="ga-mcp-editor">
      <div class="ga-mcp-editor-head">
        <div>
          <h3>{name} tools</h3>
          <p class="group-admin-help">Choose how the agent connects to these tools.</p>
        </div>
        <McpTransportControl value={type} disabled={disabled} onChange={onTypeChange} />
      </div>

      {type === 'stdio' ? (
        <McpStdioFields
          value={value as McpStdioServerDto}
          disabled={disabled}
          onChange={onChange}
        />
      ) : (
        <McpHttpFields
          value={value as McpHttpServerDto}
          disabled={disabled}
          onChange={onChange}
        />
      )}

      <details class="ga-mcp-advanced">
        <summary>Advanced settings</summary>
        <div class="ga-mcp-advanced-body">
          {type === 'stdio' ? (
            <McpKeyValueEditor
              label="Environment variables"
              value={(value as McpStdioServerDto).env ?? {}}
              disabled={disabled}
              keyPlaceholder="VARIABLE_NAME"
              valuePlaceholder="value"
              onChange={(env) => {
                const next = { ...value } as McpStdioServerDto;
                if (Object.keys(env).length) next.env = env;
                else delete next.env;
                onChange(next);
              }}
            />
          ) : (
            <McpKeyValueEditor
              label="Request headers"
              value={(value as McpHttpServerDto).headers ?? {}}
              disabled={disabled}
              keyPlaceholder="Authorization"
              valuePlaceholder="Bearer …"
              onChange={(headers) => {
                const next = { ...value } as McpHttpServerDto;
                if (Object.keys(headers).length) next.headers = headers;
                else delete next.headers;
                onChange(next);
              }}
            />
          )}
          <label class="ga-mcp-row">
            <span class="ga-mcp-row-label">Instructions</span>
            <textarea
              class="ga-mcp-textarea"
              placeholder="When should the agent use these tools?"
              rows={3}
              value={value.instructions ?? ''}
              disabled={disabled}
              onInput={(e: JSX.TargetedEvent<HTMLTextAreaElement>) => {
                const instructions = e.currentTarget.value;
                onChange({ ...value, instructions: instructions || undefined } as McpServerConfigDto);
              }}
            />
          </label>
          <label class="ga-mcp-row ga-mcp-timeout">
            <span class="ga-mcp-row-label">Timeout (ms)</span>
            <input
              type="number"
              class="ga-mcp-input"
              placeholder="60000"
              min={1000}
              max={600000}
              step={1000}
              value={value.timeout ?? ''}
              disabled={disabled}
              onInput={(e: JSX.TargetedEvent<HTMLInputElement>) => {
                const raw = e.currentTarget.value.trim();
                const timeout = raw === '' ? undefined : Number(raw);
                onChange({
                  ...value,
                  timeout: timeout != null && Number.isFinite(timeout) ? timeout : undefined,
                } as McpServerConfigDto);
              }}
            />
          </label>
        </div>
      </details>

      {auth}

      <div class="ga-mcp-test">
        <button
          type="button"
          disabled={disabled || testing || mcpServerIssue(value) !== null}
          onClick={testConnection}
        >
          {testing ? 'Testing…' : 'Test connection'}
        </button>
        <div class={`ga-mcp-test-result${testResult?.ok ? ' success' : testResult ? ' error' : ''}`} role="status" aria-live="polite">
          {testing ? 'Checking whether these tools are available…' : testResult?.ok ? (
            <>
              Connected · {testResult.tools?.length ?? 0} tool{testResult.tools?.length === 1 ? '' : 's'} · {testResult.latencyMs} ms
              {testResult.serverInfo ? <span>{testResult.serverInfo.name} {testResult.serverInfo.version}</span> : null}
            </>
          ) : testResult ? (
            <>{testResult.phase ? `${testResult.phase}: ` : ''}{testResult.error ?? 'Connection failed'}</>
          ) : null}
        </div>
      </div>

      <div class="ga-mcp-editor-danger">
        <button type="button" class="danger" disabled={disabled} onClick={() => setRemoveOpen(true)}>Remove {name} tools…</button>
      </div>

      {removeOpen ? (
        <MobileDialog
          title={`Remove ${name} tools?`}
          role="alertdialog"
          maxWidth="420px"
          onClose={() => setRemoveOpen(false)}
        >
          <div class="settings-body">
            <p class="group-admin-help">
              Remove <strong>{name} tools</strong> from this agent?
            </p>
          </div>
          <MobileDialogFooter>
            <button type="button" onClick={() => setRemoveOpen(false)}>Cancel</button>
            <button type="button" class="danger" onClick={onRemove}>Remove {name} tools</button>
          </MobileDialogFooter>
        </MobileDialog>
      ) : null}
    </div>
  );
}

function McpTransportControl({
  value,
  disabled,
  onChange,
}: {
  value: 'stdio' | 'http' | 'sse';
  disabled: boolean;
  onChange: (type: 'stdio' | 'http' | 'sse') => void;
}): JSX.Element {
  return (
    <div class="ga-mcp-transport" role="group" aria-label="Transport">
      {(['stdio', 'http', 'sse'] as const).map((type) => (
        <button
          type="button"
          key={type}
          class={value === type ? 'active' : ''}
          aria-pressed={value === type}
          disabled={disabled}
          onClick={() => onChange(type)}
        >
          {type.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function McpStdioFields({
  value,
  disabled,
  onChange,
}: {
  value: McpStdioServerDto;
  disabled: boolean;
  onChange: (next: McpStdioServerDto) => void;
}): JSX.Element {
  return (
    <>
      <label class="ga-mcp-row">
        <span class="ga-mcp-row-label">command</span>
        <input
          type="text"
          class="ga-mcp-input"
          placeholder="e.g. npx, uvx, /usr/local/bin/my-tool"
          value={value.command ?? ''}
          disabled={disabled}
          onInput={(e: JSX.TargetedEvent<HTMLInputElement>) =>
            onChange({ ...value, command: e.currentTarget.value })
          }
        />
        {!value.command.trim() ? <span class="ga-mcp-field-error">Command is required.</span> : null}
      </label>
      <McpArgumentsEditor
        value={value.args ?? []}
        disabled={disabled}
        onChange={(args) => {
          const next = { ...value };
          if (args.length) next.args = args;
          else delete next.args;
          onChange(next);
        }}
      />
    </>
  );
}

function McpHttpFields({
  value,
  disabled,
  onChange,
}: {
  value: McpHttpServerDto;
  disabled: boolean;
  onChange: (next: McpHttpServerDto) => void;
}): JSX.Element {
  const urlInvalid = value.url && !/^https?:\/\//.test(value.url);

  return (
    <label class="ga-mcp-row">
      <span class="ga-mcp-row-label">URL</span>
      <input
        type="url"
        class={`ga-mcp-input${urlInvalid ? ' ga-mcp-input-invalid' : ''}`}
        placeholder="https://example.com/mcp"
        value={value.url ?? ''}
        disabled={disabled}
        onInput={(e: JSX.TargetedEvent<HTMLInputElement>) =>
          onChange({ ...value, url: e.currentTarget.value })
        }
      />
      {!value.url.trim() ? <span class="ga-mcp-field-error">URL is required.</span> : null}
      {urlInvalid ? <span class="ga-mcp-field-error">URL must start with http:// or https://.</span> : null}
    </label>
  );
}

function McpArgumentsEditor({
  value,
  disabled,
  onChange,
}: {
  value: string[];
  disabled: boolean;
  onChange: (next: string[]) => void;
}): JSX.Element {
  const [draft, setDraft] = useState('');
  function add(): void {
    if (!draft) return;
    onChange([...value, draft]);
    setDraft('');
  }
  return (
    <div class="ga-mcp-collection">
      <span class="ga-mcp-row-label">Arguments</span>
      {value.map((argument, index) => (
        <div class="ga-mcp-collection-row" key={`${index}-${argument}`}>
          <input
            type="text"
            class="ga-mcp-input"
            aria-label={`Argument ${index + 1}`}
            value={argument}
            disabled={disabled}
            onInput={(e: JSX.TargetedEvent<HTMLInputElement>) => {
              const next = [...value];
              next[index] = e.currentTarget.value;
              onChange(next);
            }}
          />
          <button type="button" class="icon-btn" aria-label={`Remove argument ${index + 1}`} disabled={disabled} onClick={() => onChange(value.filter((_, i) => i !== index))}>{'\u2715'}</button>
        </div>
      ))}
      <div class="ga-mcp-collection-row">
        <input
          type="text"
          class="ga-mcp-input"
          aria-label="New argument"
          placeholder="Add an argument"
          value={draft}
          disabled={disabled}
          onInput={(e: JSX.TargetedEvent<HTMLInputElement>) => setDraft(e.currentTarget.value)}
          onKeyDown={(e: JSX.TargetedKeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') { e.preventDefault(); add(); }
          }}
        />
        <button type="button" disabled={disabled || !draft} onClick={add}>Add</button>
      </div>
    </div>
  );
}

function McpKeyValueEditor({
  label,
  value,
  disabled,
  keyPlaceholder,
  valuePlaceholder,
  onChange,
}: {
  label: string;
  value: Record<string, string>;
  disabled: boolean;
  keyPlaceholder: string;
  valuePlaceholder: string;
  onChange: (next: Record<string, string>) => void;
}): JSX.Element {
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const trimmedKey = newKey.trim();
  const duplicate = trimmedKey !== '' && trimmedKey in value;
  function add(): void {
    if (!trimmedKey || duplicate) return;
    onChange({ ...value, [trimmedKey]: newValue });
    setNewKey('');
    setNewValue('');
  }
  return (
    <div class="ga-mcp-collection">
      <span class="ga-mcp-row-label">{label}</span>
      {Object.entries(value).map(([key, entryValue]) => (
        <div class="ga-mcp-kv-row" key={key}>
          <code title={key}>{key}</code>
          <input
            type="text"
            class="ga-mcp-input"
            aria-label={`${key} value`}
            value={entryValue}
            disabled={disabled}
            onInput={(e: JSX.TargetedEvent<HTMLInputElement>) => onChange({ ...value, [key]: e.currentTarget.value })}
          />
          <button
            type="button"
            class="icon-btn"
            aria-label={`Remove ${key}`}
            disabled={disabled}
            onClick={() => {
              const next = { ...value };
              delete next[key];
              onChange(next);
            }}
          >
            {'\u2715'}
          </button>
        </div>
      ))}
      <div class="ga-mcp-kv-row ga-mcp-kv-new">
        <input type="text" class="ga-mcp-input" aria-label={`New ${label} key`} placeholder={keyPlaceholder} value={newKey} disabled={disabled} onInput={(e: JSX.TargetedEvent<HTMLInputElement>) => setNewKey(e.currentTarget.value)} />
        <input
          type="text"
          class="ga-mcp-input"
          aria-label={`New ${label} value`}
          placeholder={valuePlaceholder}
          value={newValue}
          disabled={disabled}
          onInput={(e: JSX.TargetedEvent<HTMLInputElement>) => setNewValue(e.currentTarget.value)}
          onKeyDown={(e: JSX.TargetedKeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') { e.preventDefault(); add(); }
          }}
        />
        <button type="button" disabled={disabled || !trimmedKey || duplicate} onClick={add}>Add</button>
      </div>
      {duplicate ? <span class="ga-mcp-field-error">That key already exists.</span> : null}
    </div>
  );
}